import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  IconButton,
  InputAdornment,
  Typography,
  Button,
  Box,
  Stack,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close"; // ← Added for close button
import { useSnackbar } from "../../App";
import { getAllBranchLists } from "../../service/branchService";
import { getAllRoles } from "../../service/roleService";
import Color from "../../components/Color";

const defaultFormData = {
  name: "",
  email: "",
  mobile: "",
  password: "",
  password_confirmation: "",
  branch_id: "",
  role_id: "",
  status: true,
};

const passwordRequirements = [
  { label: "Minimum 8 characters", regex: /^.{8,}$/ },
  { label: "At least 1 uppercase letter", regex: /[A-Z]/ },
  { label: "At least 1 number", regex: /[0-9]/ },
  { label: "At least 1 special character", regex: /[!@#$%^&*()_\-+=~`{}\[\]:;"'<>,.?/\\|]/ },
];

const isPasswordValid = (password) =>
  passwordRequirements.every((req) => req.regex.test(password));

const getPasswordStrengthAndHints = (password) => {
  if (!password) {
    return { label: "", color: "gray", hints: passwordRequirements.map((req) => req.label) };
  }
  const metCount = passwordRequirements.filter((req) => req.regex.test(password)).length;
  const hints = passwordRequirements
    .filter((req) => !req.regex.test(password))
    .map((req) => req.label);
  if (metCount <= 1) return { label: "Weak", color: "error.main", hints };
  if (metCount <= 3) return { label: "Medium", color: "warning.main", hints };
  return { label: "Strong", color: "success.main", hints: [] };
};

const AddUser = ({ open, onClose, formData, setFormData, onSubmit }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { showSnackbar } = useSnackbar();

  const [branches, setBranches] = useState([]);
  const [roles, setRoles] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordStrengthData, setPasswordStrengthData] = useState(
    getPasswordStrengthAndHints("")
  );

  // Refs for auto-focus
  const emailInputRef = useRef(null);
  const mobileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const fetchData = async () => {
      try {
        const [roleRes, branchRes] = await Promise.all([
          getAllRoles(),
          getAllBranchLists(),
        ]);
        setRoles(Array.isArray(roleRes?.data?.data) ? roleRes.data.data : []);
        setBranches(Array.isArray(branchRes?.data?.data) ? branchRes.data.data : []);
      } catch (err) {
        showSnackbar("Failed to load data", "error");
      }
    };
    fetchData();
  }, [open, showSnackbar]);

  useEffect(() => {
    setPasswordStrengthData(getPasswordStrengthAndHints(formData.password || ""));
  }, [formData.password]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === "checkbox" ? checked : value;

    if (name === "name") {
      const filtered = value.replace(/[^a-zA-Z ]/g, "");
      const limited = filtered.slice(0, 50);
      newValue = limited;

      if (!/^[a-zA-Z ]*$/.test(value)) {
        setFormErrors((prev) => ({ ...prev, name: ["Only letters are allowed"] }));
      } else if (limited.length > 50) {
        setFormErrors((prev) => ({ ...prev, name: ["Name must not exceed 50 characters"] }));
      } else {
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.name;
          return newErrors;
        });
      }
    } else if (name === "email") {
      newValue = value.toLowerCase().replace(/[^a-z0-9@._-]/g, "");
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!newValue.trim()) {
        setFormErrors((prev) => ({ ...prev, email: ["Email is required"] }));
      } else if (!emailRegex.test(newValue)) {
        setFormErrors((prev) => ({ ...prev, email: ["Invalid email format"] }));
      } else {
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.email;
          return newErrors;
        });
      }
    } else if (name === "mobile") {
      newValue = value.replace(/\D/g, "").slice(0, 10);
      if (newValue.length === 10) {
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.mobile;
          return newErrors;
        });
      } else if (newValue.length > 0) {
        setFormErrors((prev) => ({ ...prev, mobile: ["Mobile must be exactly 10 digits"] }));
      } else {
        setFormErrors((prev) => ({ ...prev, mobile: ["Mobile is required"] }));
      }
    } else if (name === "password") {
      newValue = value.replace(/\s/g, "");
    } else if (name === "password_confirmation") {
      newValue = value.replace(/\s/g, "");
      if (newValue !== formData.password) {
        setFormErrors((prev) => ({
          ...prev,
          password_confirmation: ["Passwords do not match"],
        }));
      } else {
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.password_confirmation;
          return newErrors;
        });
      }
    } else if (name === "branch_id" || name === "role_id") {
      if (!value) {
        setFormErrors((prev) => ({ ...prev, [name]: ["This field is required"] }));
      } else {
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    }

    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const validate = () => {
    const errors = {};

    if (!formData.name?.trim()) errors.name = ["Name is required"];
    else if (!/^[a-zA-Z ]+$/.test(formData.name)) errors.name = ["Only letters are allowed"];
    else if (formData.name.length > 50) errors.name = ["Name must not exceed 50 characters"];

    if (!formData.email?.trim()) errors.email = ["Email is required"];
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = ["Invalid email format"];

    if (!formData.mobile) errors.mobile = ["Mobile is required"];
    else if (!/^\d{10}$/.test(formData.mobile)) errors.mobile = ["Mobile must be exactly 10 digits"];

    if (!formData.role_id) errors.role_id = ["Please select a role"];

    const selectedRole = roles.find((r) => r.id === formData.role_id);
    const isManager = selectedRole?.name?.toLowerCase() === "manager";
    if (isManager && !formData.branch_id) {
      errors.branch_id = ["Branch is required for Manager role"];
    }

    if (!formData.id) {
      if (!isPasswordValid(formData.password)) {
        errors.password = ["Password does not meet requirements"];
      }
      if (formData.password !== formData.password_confirmation) {
        errors.password_confirmation = ["Passwords do not match"];
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleClose = () => {
    setFormData(defaultFormData);
    setFormErrors({});
    setShowPassword(false);
    setShowConfirm(false);
    setPasswordStrengthData(getPasswordStrengthAndHints(""));
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      showSnackbar("Please fix validation errors before submitting.", "error");
      return;
    }

    setLoading(true);
    try {
      const response = await onSubmit();

      if (response?.status === "success") {
        showSnackbar(formData.id ? "User updated successfully!" : "User created successfully!", "success");
        handleClose();
      }
    } catch (err) {
      console.error("Submit failed:", err);

      const serverErrors = err?.response?.data?.errors || {};
      const message = err?.response?.data?.message || "Failed to save user.";

      if (Object.keys(serverErrors).length > 0) {
        const formattedErrors = {};
        Object.keys(serverErrors).forEach((field) => {
          formattedErrors[field] = serverErrors[field];
        });

        setFormErrors((prev) => ({ ...prev, ...formattedErrors }));

        if (formattedErrors.email && emailInputRef.current) {
          emailInputRef.current.focus();
        } else if (formattedErrors.mobile && mobileInputRef.current) {
          mobileInputRef.current.focus();
        }

        showSnackbar(message, "error");
      } else {
        showSnackbar(message, "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const { label: passwordStrength, color: strengthColor, hints } = passwordStrengthData;
  const isEdit = !!formData.id;

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          if (Object.keys(formErrors).length > 0) {
            showSnackbar("Please fix validation errors before closing.", "error");
            return;
          }
          handleClose();
        } else {
          handleClose();
        }
      }}
      disableEscapeKeyDown={Object.keys(formErrors).length > 0}
      PaperProps={{
        sx: {
          width: isMobile ? "90vw" : "1000px",
          maxWidth: "100vw",
          borderRadius: "8px",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: isMobile ? "1rem" : "1.25rem",
          position: "relative",
          pr: 6,
        }}
      >
        {isEdit ? "Edit User" : "Add User"}

        {/* Close Icon Button */}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={Object.keys(formErrors).length > 0}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          px: isMobile ? 2 : 3,
          py: isMobile ? 1 : 2,
          maxHeight: isMobile ? "70vh" : "auto",
          overflowY: "auto",
        }}
      >
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 2, mb: 2 }}>
            <Box>
              <Typography sx={{ mb: 1, fontWeight: "bold", color: Color.label.main, fontSize: isMobile ? "12px" : "13px" }}>
                Name*
              </Typography>
              <TextField
                name="name"
                fullWidth
                value={formData.name || ""}
                onChange={handleChange}
                placeholder="Enter name"
                error={!!formErrors.name}
                helperText={formErrors.name?.[0] || `${formData.name?.length || 0}/50`}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                inputProps={{ maxLength: 50 }}
                sx={{
                  "& .MuiInputLabel-root": { display: "none" },
                  "& .MuiInputBase-input": { fontSize: isMobile ? "0.85rem" : "1rem" },
                }}
              />
            </Box>

            <Box>
              <Typography sx={{ mb: 1, fontWeight: "bold", color: Color.label.main, fontSize: isMobile ? "12px" : "13px" }}>
                Email*
              </Typography>
              <TextField
                name="email"
                fullWidth
                value={formData.email || ""}
                onChange={handleChange}
                placeholder="Enter email"
                error={!!formErrors.email}
                helperText={formErrors.email?.[0]}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                inputRef={emailInputRef}
                sx={{
                  "& .MuiInputLabel-root": { display: "none" },
                  "& .MuiInputBase-input": { fontSize: isMobile ? "0.85rem" : "1rem" },
                }}
              />
            </Box>

            <Box>
              <Typography sx={{ mb: 1, fontWeight: "bold", color: Color.label.main, fontSize: isMobile ? "12px" : "13px" }}>
                Mobile*
              </Typography>
              <TextField
                name="mobile"
                fullWidth
                value={formData.mobile || ""}
                onChange={handleChange}
                placeholder="Enter 10-digit mobile"
                error={!!formErrors.mobile}
                helperText={formErrors.mobile?.[0]}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                inputProps={{ maxLength: 10, inputMode: "numeric" }}
                type="tel"
                inputRef={mobileInputRef}
                sx={{
                  "& .MuiInputLabel-root": { display: "none" },
                  "& .MuiInputBase-input": { fontSize: isMobile ? "0.85rem" : "1rem" },
                }}
              />
            </Box>
          </Box>

          {!isEdit && (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 2 }}>
              <Box>
                <Typography sx={{ mb: 1, fontWeight: "bold", color: Color.label.main, fontSize: isMobile ? "12px" : "13px" }}>
                  Password*
                </Typography>
                <TextField
                  name="password"
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  value={formData.password || ""}
                  onChange={handleChange}
                  placeholder="Enter strong password"
                  error={!!formErrors.password}
                  helperText={
                    <Stack spacing={0.5}>
                      {passwordStrength && (
                        <Typography variant="caption" sx={{ fontWeight: "bold", color: strengthColor }}>
                          Strength: {passwordStrength}
                        </Typography>
                      )}
                      {hints.length > 0 && (
                        <Box>
                          {hints.map((hint, i) => (
                            <Typography key={i} variant="caption" color="text.secondary" fontSize="10px">
                              ○ {hint}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Stack>
                  }
                  variant="outlined"
                  InputLabelProps={{ shrink: false }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiInputLabel-root": { display: "none" },
                    "& .MuiInputBase-input": { fontSize: isMobile ? "0.85rem" : "1rem" },
                  }}
                />
              </Box>

              <Box>
                <Typography sx={{ mb: 1, fontWeight: "bold", color: Color.label.main, fontSize: isMobile ? "12px" : "13px" }}>
                  Confirm Password*
                </Typography>
                <TextField
                  name="password_confirmation"
                  type={showConfirm ? "text" : "password"}
                  fullWidth
                  value={formData.password_confirmation || ""}
                  onChange={handleChange}
                  placeholder="Confirm password"
                  error={!!formErrors.password_confirmation}
                  helperText={formErrors.password_confirmation?.[0]}
                  variant="outlined"
                  InputLabelProps={{ shrink: false }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end">
                          {showConfirm ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiInputLabel-root": { display: "none" },
                    "& .MuiInputBase-input": { fontSize: isMobile ? "0.85rem" : "1rem" },
                  }}
                />
              </Box>
            </Box>
          )}

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <Box>
              <Typography sx={{ mb: 1, fontWeight: "bold", color: Color.label.main, fontSize: isMobile ? "12px" : "13px" }}>
                Role*
              </Typography>
              <FormControl fullWidth error={!!formErrors.role_id}>
                <Select
                  name="role_id"
                  value={formData.role_id ?? ""}
                  onChange={handleChange}
                  displayEmpty
                  sx={{ height: 40 }}
                >
                  <MenuItem value="" disabled>Select role</MenuItem>
                  {roles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>
                  ))}
                </Select>
                {formErrors.role_id && <FormHelperText>{formErrors.role_id[0]}</FormHelperText>}
              </FormControl>
            </Box>

            {roles.find((r) => r.id === formData.role_id)?.name?.toLowerCase() === "manager" && (
              <Box>
                <Typography sx={{ mb: 1, fontWeight: "bold", color: Color.label.main, fontSize: isMobile ? "12px" : "13px" }}>
                  Branch*
                </Typography>
                <FormControl fullWidth error={!!formErrors.branch_id}>
                  <Select
                    name="branch_id"
                    value={formData.branch_id ?? ""}
                    onChange={handleChange}
                    displayEmpty
                    sx={{ height: 40 }}
                  >
                    <MenuItem value="" disabled>Select branch</MenuItem>
                    {branches.map((b) => (
                      <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                    ))}
                  </Select>
                  {formErrors.branch_id && <FormHelperText>{formErrors.branch_id[0]}</FormHelperText>}
                </FormControl>
              </Box>
            )}
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography sx={{ mb: 1, fontWeight: "bold", color: Color.label.main, fontSize: isMobile ? "12px" : "13px" }}>
              Status
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.status}
                  onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.checked }))}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: Color.primary.main },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: Color.primary.main },
                  }}
                />
              }
              label={
                <Typography sx={{ color: formData.status ? Color.primary.main : "text.secondary", fontWeight: "bold" }}>
                  {formData.status ? "Active" : "Inactive"}
                </Typography>
              }
            />
          </Box>

          <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
            <Button
              onClick={handleClose}
              sx={{
                background: "none",
                color: Color.secondary.main,
                border: "1px solid",
                borderColor: Color.secondary.main,
                width: isMobile ? "100%" : "auto",
                height: isMobile ? "40px" : "45px",
                fontSize: isMobile ? "0.85rem" : "1rem",
                padding: isMobile ? "10px" : "6px 20px",
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              sx={{
                background: Color.primary.main,
                color: "white",
                width: isMobile ? "100%" : "auto",
                height: isMobile ? "40px" : "45px",
                fontSize: isMobile ? "0.85rem" : "1rem",
                padding: isMobile ? "10px" : "6px 20px",
                "&:hover": { background: Color.primary.dark },
              }}
            >
              {loading ? "Saving..." : isEdit ? "Update" : "Create"}
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddUser;